import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
	deleteKSK2040_R01,
	getKSK2040_R01AllCount,
	getKSK2040_R01DetailList,
	getKSK2040_R01List,
	patchKSK2040_R01,
	postKSK2040_R01,
} from "./Api_KSK2040_R01";
import {
	defaultKSK2040_R01HeaderDto,
	KSK2040_R01DetailDto,
	KSK2040_R01Dto,
	KSK2040_R01HeaderDto,
	KSK2040_R01_INSPECT_TYPE,
	KSK2040_R01_VALUE_FIELDS,
} from "./Dto_KSK2040_R01";
import { useKSK2040_R01Store } from "./Store_KSK2040_R01";
import {
	createKSK2040_R01SaveDetail,
	hasKSK2040_R01InspectionValue,
	normalizeKSK2040_R01InspectionDetail,
	validateKSK2040_R01InspectionValue,
} from "./Util_KSK2040_R01";

interface ContentKSK2040R01Props {
	gridRef: React.RefObject<AgGridReact<KSK2040_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<KSK2040_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

const getKSK2040_R01DetailEditSignature = (detailList: KSK2040_R01DetailDto[]) =>
	JSON.stringify(detailList.map((detail) => [
		detail.valueIndex,
		...KSK2040_R01_VALUE_FIELDS.map((field) => String(detail[field] ?? "")),
		String(detail.remark ?? ""),
	]));

const hasKSK2040_R01PendingChanges = () => {
	const state = useKSK2040_R01Store.getState();
	const selected = state.selectedKSK2040_R01;
	if (!selected) return false;

	const edit = state.editKSK2040_R01;
	const isHeaderChanged =
		edit.inspectorEmp !== selected.inspectorEmp
		|| edit.approvorEmpno !== selected.approvorEmpno
		|| formatDate(edit.inspectDt) !== formatDate(selected.inspectDt);
	const isDetailChanged =
		getKSK2040_R01DetailEditSignature(state.KSK2040_R01DetailList)
		!== getKSK2040_R01DetailEditSignature(state.originalKSK2040_R01DetailList);

	return isHeaderChanged || isDetailChanged;
};

export const Content_KSK2040_R01 = ({
	gridRef,
	detailGridRef,
	setMessage,
}: ContentKSK2040R01Props) => {
	const {
		KSK2040_R01Filter,
		setKSK2040_R01PastFilter,
		setKSK2040_R01List,
		selectedKSK2040_R01,
		setSelectedKSK2040_R01,
		editKSK2040_R01,
		setEditKSK2040_R01,
		KSK2040_R01DetailList,
		setKSK2040_R01DetailList,
		setOriginalKSK2040_R01DetailList,
		setAllCount,
		setScrollTop,
		setDetailScrollTop,
		initData,
	} = useKSK2040_R01Store();
	const { inspectionTypeList } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const detailRequestIdRef = useRef(0);

	const clearSelectionData = useCallback(() => {
		detailRequestIdRef.current += 1;
		setSelectedKSK2040_R01(undefined);
		setEditKSK2040_R01({ ...defaultKSK2040_R01HeaderDto });
		setKSK2040_R01DetailList([]);
		setOriginalKSK2040_R01DetailList([]);
	}, [
		setEditKSK2040_R01,
		setKSK2040_R01DetailList,
		setOriginalKSK2040_R01DetailList,
		setSelectedKSK2040_R01,
	]);

	const applyFetchedData = useCallback((data: KSK2040_R01Dto) => {
		const typeName = inspectionTypeList.find((item) =>
			item.minorCd === data.header.inspectType)?.codeNm || "";
		const header = {
			...data.header,
			inspectTypeNm: typeName,
		};
		const details = data.detailList.map(normalizeKSK2040_R01InspectionDetail);
		setSelectedKSK2040_R01(header);
		setEditKSK2040_R01(header);
		setKSK2040_R01DetailList(details);
		setOriginalKSK2040_R01DetailList(details);
		if (details.length === 0) {
			showWarning("선택한 품목에 적용된 공정 검사항목이 없습니다.");
		}
	}, [
		inspectionTypeList,
		setEditKSK2040_R01,
		setKSK2040_R01DetailList,
		setOriginalKSK2040_R01DetailList,
		setSelectedKSK2040_R01,
	]);

	const fetchDetailData = useCallback(async (
		target: KSK2040_R01HeaderDto,
		inspectType = KSK2040_R01_INSPECT_TYPE,
	) => {
		const requestId = ++detailRequestIdRef.current;
		const response = await getKSK2040_R01DetailList(
			target.productResultNo,
			inspectType,
		);
		if (requestId !== detailRequestIdRef.current) return;
		applyFetchedData(response.data.data);
	}, [applyFetchedData]);

	const handleInspectTypeChange = useCallback(async (inspectType: string) => {
		if (!selectedKSK2040_R01 || inspectType === editKSK2040_R01.inspectType) return;
		detailGridRef.current?.api.stopEditing();
		if (hasKSK2040_R01PendingChanges()) {
			showWarning("변경 내용을 저장하거나 초기화한 후 검사구분을 변경해 주세요.");
			return;
		}
		if (!inspectType) {
			showWarning("검사구분을 선택해주세요.");
			return;
		}

		setLoadingSpinner(true);
		try {
			setDetailScrollTop(null);
			await fetchDetailData(selectedKSK2040_R01, inspectType);
			detailGridRef.current?.api.ensureIndexVisible(0, "top");
		} catch {
			showError("검사구분별 검사항목 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		detailGridRef,
		editKSK2040_R01.inspectType,
		fetchDetailData,
		selectedKSK2040_R01,
		setDetailScrollTop,
		setLoadingSpinner,
	]);

	const executeDataFetch = useCallback(async (option: "search" | "refetch") => {
		const [listResponse, countResponse] = await Promise.all([
			getKSK2040_R01List(option, KSK2040_R01Filter),
			getKSK2040_R01AllCount(KSK2040_R01Filter),
		]);
		setKSK2040_R01List(listResponse.data.data);
		setAllCount(countResponse.data.data);
		setKSK2040_R01PastFilter({ ...KSK2040_R01Filter });
		return { list: listResponse.data.data, allCount: countResponse.data.data };
	}, [
		KSK2040_R01Filter,
		setAllCount,
		setKSK2040_R01List,
		setKSK2040_R01PastFilter,
	]);

	const fetchData = useCallback(async () => {
		gridRef.current?.api.stopEditing();
		detailGridRef.current?.api.stopEditing();
		if (hasKSK2040_R01PendingChanges()) {
			showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			clearSelectionData();
			setScrollTop(null);
			setDetailScrollTop(null);
			const { list, allCount } = await executeDataFetch("search");
			if (list.length === 0) {
				setMessage(`조회 0건 / 전체 ${allCount.toLocaleString()}건`);
				showWarning("조회된 생산실적이 없습니다.");
				return;
			}
			await fetchDetailData(list[0]);
			gridRef.current?.api.ensureIndexVisible(0, "top");
			setMessage(`조회완료 (${list.length.toLocaleString()}/${allCount.toLocaleString()}건)`);
		} catch {
			showError("공정검사 대상 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		detailGridRef,
		executeDataFetch,
		gridRef,
		fetchDetailData,
		setDetailScrollTop,
		setLoadingSpinner,
		setMessage,
		setScrollTop,
	]);

	const fetchOne = useCallback(async (target: KSK2040_R01HeaderDto) => {
		detailGridRef.current?.api.stopEditing();
		const selected = useKSK2040_R01Store.getState().selectedKSK2040_R01;
		const isSameTarget = selected?.productResultNo === target.productResultNo;
		if (hasKSK2040_R01PendingChanges()) {
			showWarning(
				isSameTarget
					? "변경 내용을 저장하거나 초기화한 후 선택을 해제해 주세요."
					: "변경 내용을 저장하거나 초기화한 후 다른 생산실적을 선택해 주세요.",
			);
			return;
		}
		if (isSameTarget) {
			clearSelectionData();
			return;
		}
		setLoadingSpinner(true);
		try {
			setDetailScrollTop(null);
			await fetchDetailData(target);
			detailGridRef.current?.api.ensureIndexVisible(0, "top");
		} catch {
			showError("공정검사 상세 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		detailGridRef,
		fetchDetailData,
		setDetailScrollTop,
		setLoadingSpinner,
	]);

	const buildSaveData = useCallback((): KSK2040_R01Dto | undefined => {
		if (!selectedKSK2040_R01) {
			showWarning("검사할 생산실적을 선택해주세요.");
			return undefined;
		}
		if (!validateString(editKSK2040_R01.inspectorEmp)) {
			showWarning("검사자를 선택해주세요.");
			return undefined;
		}
		if (!editKSK2040_R01.inspectDt) {
			showWarning("검사일자를 선택해주세요.");
			return undefined;
		}
		if (KSK2040_R01DetailList.length === 0) {
			showWarning("저장할 검사항목이 없습니다.");
			return undefined;
		}
		const details = KSK2040_R01DetailList.map(normalizeKSK2040_R01InspectionDetail);
		if (!hasKSK2040_R01InspectionValue(details)) {
			showWarning("검사결과를 한 개 이상 입력해주세요.");
			return undefined;
		}
		const invalidDetail = details.find((detail) =>
			!validateKSK2040_R01InspectionValue(detail));
		if (invalidDetail) {
			showWarning(`${invalidDetail.inspItemNm || invalidDetail.itemCd} 검사결과 형식을 확인해주세요.`);
			return undefined;
		}
		return {
				header: {
					...editKSK2040_R01,
					inspectDt: formatDate(editKSK2040_R01.inspectDt),
			},
			detailList: details.map(createKSK2040_R01SaveDetail),
		};
	}, [KSK2040_R01DetailList, editKSK2040_R01, selectedKSK2040_R01]);

	const handleSaveClick = useCallback(async () => {
		detailGridRef.current?.api.stopEditing();
		const payload = buildSaveData();
		if (!payload) return;
		setLoadingSpinner(true);
		try {
			const response = payload.header.inspectNo
				? await patchKSK2040_R01(payload)
				: await postKSK2040_R01(payload);
			applyFetchedData(response.data.data);
			await executeDataFetch("refetch");
			showSuccess("공정검사 결과를 저장했습니다.");
		} catch {
			showError("공정검사 결과 저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [applyFetchedData, buildSaveData, detailGridRef, executeDataFetch, setLoadingSpinner]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedKSK2040_R01 || !editKSK2040_R01.inspectNo) {
			showWarning("삭제할 공정검사 결과가 없습니다.");
			return;
		}
		const productResultNo = editKSK2040_R01.productResultNo;
		setLoadingSpinner(true);
		try {
			await deleteKSK2040_R01([editKSK2040_R01]);
			const { list } = await executeDataFetch("refetch");
			const target = list.find((item) => item.productResultNo === productResultNo);
			if (target) await fetchDetailData(target);
			else clearSelectionData();
			showSuccess("공정검사 결과를 삭제했습니다. 생산실적은 삭제되지 않습니다.");
		} catch {
			showError("공정검사 결과 삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		editKSK2040_R01,
		executeDataFetch,
		fetchDetailData,
		selectedKSK2040_R01,
		setLoadingSpinner,
	]);

	const initContainerData = useCallback(() => {
		gridRef.current?.api.stopEditing();
		detailGridRef.current?.api.stopEditing();
		initData();
		setMessage("");
	}, [detailGridRef, gridRef, initData, setMessage]);

	return {
		fetchData,
		fetchOne,
		handleInspectTypeChange,
		handleSaveClick,
		handleDeleteClick,
		initContainerData,
	};
};
